import { AfterViewInit, Component, effect, inject, signal, ElementRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ArenaService } from '../../../services/arenas/arena.service';
import { MediaService } from '../../../services/media/media.service';
import { ChatService } from '../../../services/chat/chat.service';

@Component({
  selector: 'app-create-lobby-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './create-lobby-modal.html',
  styleUrl: './create-lobby-modal.scss',
})
export class CreateLobbyModal implements AfterViewInit {
  private arenaService = inject(ArenaService);
  private chatService = inject(ChatService);
  private media = inject(MediaService);
  private router = inject(Router);
  private el = inject(ElementRef);

  public arenaIcons = this.media.arenaIcons;
  public selectedIcon = signal<any>(null);
  public defaultAvatarUrl = '/images/logo_1_2.png';

  public lobbyForm = new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50)
      ]
    }),
    description: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.maxLength(200)
      ]
    }),
    password: new FormControl('', { nonNullable: true }),
    isPublic: new FormControl(true, { nonNullable: true }),
    isStatic: new FormControl(false, { nonNullable: true }),
    hasVoice: new FormControl(true, { nonNullable: true }),
    hasVideo: new FormControl(false, { nonNullable: true }),
    hasImages: new FormControl(true, { nonNullable: true }),
    motd: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.maxLength(100)
      ]
    }),
    url: new FormControl('', { nonNullable: true }),
    avatarURL: new FormControl('/images/logo_1_2.png', {
      nonNullable: true,
      validators: [
        Validators.required
      ]
    })
  });

  constructor() {}

  /*
   * Initialize media retrieval after view initialization.
   */
  ngAfterViewInit(): void {
    this.media.getArenaMedia('', 2);
  }

  /*
   * Select a custom room icon from uploaded media.
   */
  public selectIcon(icon: any) {
    const avatarURL = icon.url || `https://usercontent.h5arena.com/arenas/${icon.guid}${icon.fileExtension}`;
    this.lobbyForm.controls.avatarURL.setValue(avatarURL);
    this.selectedIcon.set(icon);
  }

  /*
   * Create a new lobby with default or selected icon.
   */
  public create() {
    this.lobbyForm.markAllAsTouched();
    if (this.lobbyForm.valid) {
      const formValue = this.lobbyForm.getRawValue();
      const model = {
        title: formValue.title,
        description: formValue.description,
        password: formValue.password,
        isProtected: formValue.password !== '',
        isPublic: formValue.isPublic,
        isStatic: formValue.isStatic,
        hasVoice: formValue.hasVoice,
        hasVideo: formValue.hasVideo,
        hasImages: formValue.hasImages,
        hasWebView: false,
        motd: formValue.motd,
        url: formValue.url,
        avatarURL: formValue.avatarURL || this.defaultAvatarUrl,
      };

      this.arenaService.create(model).subscribe({
        next: (response: any) => {
          console.log('Lobby created successfully:', response);
          // Close the modal
          const closeButton = this.el.nativeElement.querySelector('[data-bs-dismiss="modal"]');
          if (closeButton) {
            closeButton.click();
          }
          // Join on success: connect via chatService and navigate to chat view
          const guid = response.guid || response.id;
          if (guid) {
            if (this.chatService.isConnected()) {
              this.chatService.join(guid, model.password);
            } else {
              this.chatService.connect(guid, model.password);
            }
            this.router.navigate(['/home/chat', guid]);
          }
        },
        error: (error) => {
          console.error('Failed to create lobby:', error);
        }
      });
    }
  }
}
